import { Component } from '@theme/component';
import { API_ENDPOINTS } from '@theme/api-endpoints';
import { trackFormSubmit } from '@theme/analytics';

/**
 * <search-feedback> — collapsed CTA card that expands to an inline feedback
 * form and POSTs it to the Sugar CRM site-surveys app proxy.
 *
 * Root attributes:
 *   data-error-message — translated error string shown on failure
 *
 * Search-page feedback is identified by SurveyTypeID 19. trackingID is sent
 * additionally when FullStory is running.
 *
 * The endpoint is a relative path — the store's domain proxies
 * /shopify-app-proxy/* in every environment, which also avoids CORS.
 */

const ENDPOINT = `${API_ENDPOINTS.siteSurvey}/null`;
const SEARCH_SURVEY_TYPE_ID = '19';

/**
 * Fallback session id, verified against the live shop.com submission. The
 * visitor's own session comes from the legacy session cookie, whose name is
 * "CATALOGCITY_SSNLIVE" followed by MA's site id (e.g. CATALOGCITY_SSNLIVE260
 * on shop.com) — we match it by prefix, so the suffix never needs to be known.
 * The cookie is set on the shop.com domain, readable only once the storefront
 * runs under it.
 */
const FALLBACK_USER_SESSION_ID = '4537947763';

/**
 * First cookie (as a raw `name=value` string) whose name starts with the
 * given prefix.
 * @param {string} prefix
 * @returns {string}
 */
const findCookie = (prefix) => document.cookie.split('; ').find((cookie) => cookie.startsWith(prefix)) || '';

/**
 * Current FullStory session URL when FS is running on the page — the same
 * value the live site sends as trackingID. Supports the v2 (`FS('getSession')`)
 * and v1 (`FS.getCurrentSessionURL()`) APIs.
 * @returns {string}
 */
const getFullStorySessionUrl = () => {
  try {
    const fs = window.FS;
    if (typeof fs === 'function') return fs('getSession') || '';
    if (fs && typeof fs.getCurrentSessionURL === 'function') return fs.getCurrentSessionURL(true) || '';
  } catch {
    // FullStory unavailable or blocked
  }
  return '';
};

class SearchFeedback extends Component {
  requiredRefs = ['toggleBtn', 'panel', 'formWrap', 'textarea', 'submitBtn', 'errorEl', 'successEl'];

  /** Reveal the form panel and move focus to the textarea */
  openPanel = () => {
    const { panel, toggleBtn, textarea } = this.refs;
    panel.classList.remove('tw:hidden');
    toggleBtn.setAttribute('aria-expanded', 'true');
    textarea.focus();
  };

  /** Enable the submit button only when the textarea has content */
  updateSubmitState = () => {
    const { submitBtn, textarea } = this.refs;
    submitBtn.disabled = textarea.value.trim() === '';
  };

  /**
   * Submit the feedback to the site-surveys API.
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    event.preventDefault();

    const { textarea, submitBtn, formWrap, successEl } = this.refs;
    const comments = textarea.value.trim();
    if (!comments) return;

    submitBtn.disabled = true;
    this.#hideError();

    // The visitor's own session id when the cookie is readable, the verified
    // fallback otherwise; trackingID only when FullStory provides one.
    const sessionCookie = findCookie('CATALOGCITY_SSNLIVE');
    const userSessionId = sessionCookie
      ? decodeURIComponent(sessionCookie.slice(sessionCookie.indexOf('=') + 1))
      : FALLBACK_USER_SESSION_ID;
    const trackingID = getFullStorySessionUrl();

    const params = new URLSearchParams({
      userSessionId,
      comments,
      httpreferrer: window.location.href,
      SurveyTypeID: SEARCH_SURVEY_TYPE_ID,
    });
    if (trackingID) params.set('trackingID', trackingID);

    try {
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (!response.ok) throw new Error(`Survey request failed: ${response.status}`);

      // Success — the API returns 200 with an empty body
      trackFormSubmit(event.target.closest('form'));

      formWrap.classList.add('tw:hidden');
      successEl.classList.remove('tw:hidden');
      successEl.classList.add('tw:flex');
    } catch {
      this.#showError();
    }
  };

  #showError() {
    const { errorEl, submitBtn } = this.refs;
    errorEl.textContent = this.dataset.errorMessage || '';
    errorEl.classList.remove('tw:hidden');
    submitBtn.disabled = false;
  }

  #hideError() {
    const { errorEl } = this.refs;
    errorEl.classList.add('tw:hidden');
    errorEl.textContent = '';
  }
}

if (!customElements.get('search-feedback')) {
  customElements.define('search-feedback', SearchFeedback);
}
